import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  bulkBlockUnblockContractor,
  getContractorDetails,
  getContractorUserDetails,
} from "../../../actions/contractor";
import { showNotifier } from "../../../actions/ui";
import DocumentPreview from "../../../components/document-preview";
import FullScreenLoader from "../../../components/fullscreen-loader";
import { Button } from "../../../components/library/button";
import Modal from "../../../components/modal";
import { BankAccount } from "../../../dto";
import UserPlaceholder from "../../../images/profile-user.svg";
import dispatch from "../../../middleware";
import {
  ContractorActions,
  ContractorDetails,
  ContractorUserDetails,
  NotifierBgColor,
} from "../../../models";
import { ReduxState } from "../../../reducers";
import {
  getErrorMsgFromResponse,
  getParamFromUrl,
  maskString,
} from "../../../utils";
import getEffect from "../effect";

function getPrimaryBankInfo(bankInfo?: BankAccount[] | null) {
  if (!Array.isArray(bankInfo)) return;
  const filteredBankInfo = bankInfo.find(
    (eachBankInfo) => eachBankInfo?.data?.primary
  );

  if (!filteredBankInfo) return;
  return filteredBankInfo.data;
}

export default function ContractorProfile() {
  const contractorId = getParamFromUrl("contractorId");
  const storeDispatch = useDispatch();
  const loading = useSelector((state: ReduxState) => state.contractor.loading);
  const contractorDetails: ContractorDetails | null =
    useSelector((state: ReduxState) => state.contractor.contractor) ?? null;
  const contractorUserDetails: ContractorUserDetails | null =
    useSelector(
      (state: ReduxState) => state.contractor.contractorUserDetails
    ) ?? null;

  const [selfieImage, setSelfieImage] = useState<string | null>(null);
  const [aadharImage, setAadharImage] = useState<string | null>(null);
  const [isBlockModalActive, setIsBlockModalActive] = useState(false);
  const [isContractorBlocked, setIsContractorBlocked] = useState(false);

  const contractorDocumentList = [
    {
      path: "selfie",
      documentType: "SELFIE",
      action: setSelfieImage,
      status: "status",
      data: selfieImage,
      className: "col-6",
    },
    {
      documentType: "AADHAR",
      path: "address.reviewMetadata.verifiedBy",
      action: setAadharImage,
      status: "address.reviewMetadata.status",
      data: aadharImage,
      className: "col-6",
    },
  ];

  async function getPageDetails(contractorId: string) {
    try {
      await Promise.all([
        dispatch(storeDispatch, getContractorDetails(contractorId)),
        dispatch(storeDispatch, getContractorUserDetails(contractorId)),
      ]);
    } catch (error: any) {
      dispatch(
        storeDispatch,
        showNotifier(NotifierBgColor.ERROR, getErrorMsgFromResponse(error))
      );
    }
  }

  async function blockOrUnblockContractor(
    contractorId: string,
    action: ContractorActions
  ) {
    setIsBlockModalActive(false);
    if (!contractorId) return;
    try {
      await dispatch(
        storeDispatch,
        bulkBlockUnblockContractor([contractorId], action)
      );
      const successMessage = `${action}ED ContractorID: ${contractorId}`;
      dispatch(
        storeDispatch,
        showNotifier(NotifierBgColor.SUCCESS, successMessage)
      );

      if (action === ContractorActions.BLOCK)
        return setIsContractorBlocked(true);
      return setIsContractorBlocked(false);
    } catch (error) {
      dispatch(
        storeDispatch,
        showNotifier(NotifierBgColor.ERROR, getErrorMsgFromResponse(error))
      );
    }
  }

  useEffect(() => {
    if (!contractorId) return;
    getPageDetails(contractorId);
  }, [contractorId]);

  useEffect(() => {
    const contractorName = contractorDetails?.fullName ?? "";
    getEffect(contractorName, storeDispatch);
  }, [contractorDetails?.fullName, storeDispatch]);

  if (loading) return <FullScreenLoader active={loading} />;

  if (!contractorDetails)
    return (
      <h1
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Something went wrong!
      </h1>
    );

  const primaryBankData = getPrimaryBankInfo(
    contractorUserDetails?.bankAccountInfo
  );

  return (
    <>
      <div className="d-flex gap-3 justify-content-end mb-3">
        {!isContractorBlocked && (
          <Button
            variant="secondary"
            color="danger"
            size="md"
            onClick={() => {
              setIsBlockModalActive(true);
            }}
          >
            Block
          </Button>
        )}
        {isContractorBlocked && (
          <Button
            variant="secondary"
            color="brand"
            size="md"
            onClick={() =>
              blockOrUnblockContractor(
                contractorId ?? "",
                ContractorActions.UNBLOCK
              )
            }
          >
            Unblock
          </Button>
        )}
      </div>
      <div className="table-wrapper mb-3">
        <div className="d-flex">
          <span className="circle-avatar mr-3">
            <img src={UserPlaceholder} alt={contractorDetails.fullName} />
          </span>
          <p className="fw-700 primary-color m-0 mt-3">
            {contractorDetails.fullName}
          </p>
        </div>
        <div className="row mt-5">
          <div className="col-md-4">
            <p className="light-gray fw-600">Personal Details</p>

            <UserDataField
              label="Contractor ID: "
              value={contractorDetails.employersContractorId}
            />
            <UserDataField
              label="Employer ID: "
              value={contractorDetails.employerId ?? "-"}
            />
            <UserDataField
              label="Mobile number: "
              value={contractorDetails.mobile ?? "-"}
            />
            <UserDataField
              label="Email:  "
              value={contractorDetails.email ?? "-"}
            />
            <UserDataField
              label="Gender: "
              value={contractorUserDetails?.gender ?? "-"}
            />
            <UserDataField
              label="PAN: "
              value={
                contractorUserDetails?.pan?.data?.pan
                  ? maskString({
                      inputString: contractorUserDetails.pan.data.pan,
                      numCharactersToShowStart: 0,
                      numCharactersToShowEnd: 4,
                    })
                  : "-"
              }
            />
            <UserDataField
              label="Address: "
              value={
                contractorUserDetails?.address?.data
                  ? Object.values(contractorUserDetails.address.data).join(", ")
                  : "-"
              }
            />
          </div>
          <div className="col-md-4">
            <p className="light-gray fw-600">Account Details</p>
            <UserDataField
              label="Account Number: "
              value={
                primaryBankData?.accountNumber
                  ? maskString({
                      inputString: primaryBankData?.accountNumber,
                      numCharactersToShowStart: 0,
                      numCharactersToShowEnd: 4,
                    })
                  : "-"
              }
            />
            <UserDataField
              label="IFSC Code: "
              value={primaryBankData?.ifsc ?? "-"}
            />
          </div>
          <div className="col-md-4">
            <div className="row text-center">
              {contractorDocumentList.map((document) => {
                console.log("Logging: ", document.data);
                return document.data ? (
                  <DocumentPreview
                    key={document.documentType}
                    className={document.className}
                    dataSrc={document.data}
                    docName={document.documentType}
                    verificationStatus={document.status ?? "NA"}
                  />
                ) : undefined;
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="table-wrapper mb-3">
        <div style={{ width: "30%" }} className="row">
          <div className="col-sm-12">
            <p className="table-title fw-600 fs-18 m-0">Earning Details</p>
            <div className="mt-3">
              <UserDataField
                label="Earned Amount: "
                value={contractorUserDetails?.earnedAmount ?? 0}
              />
              <UserDataField
                label="Total Outstanding Amount: "
                value={contractorUserDetails?.totalOutstandingAmount ?? 0}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        active={isBlockModalActive}
        onClose={() => setIsBlockModalActive(false)}
      >
        <div className="modalWrapper">
          <button
            className="btn abs-top-right-btn light-gray"
            onClick={() => setIsBlockModalActive(false)}
          >
            x
          </button>
          <p className="primary-color fs-16 fw-700">Block Contractor</p>
          <p className="fs-14 primary-color mb-4">
            Do you want to block this contractor with Contractor ID:{" "}
            {<b>{contractorId}</b>} ?
          </p>
          <div className="d-flex">
            <button
              className="btn primary-button w-unset px-5 fs-14 mr-3 b-color-secondary"
              onClick={() =>
                blockOrUnblockContractor(
                  contractorId ?? "",
                  ContractorActions.BLOCK
                )
              }
            >
              Yes
            </button>
            <button
              className="btn outline-btn fs-14 b-color-secondary secondary-color px-5"
              onClick={() => setIsBlockModalActive(false)}
            >
              No
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
interface UserDataFiledProps {
  label: string;
  value: string | number | null | undefined;
}

function UserDataField({ label, value }: UserDataFiledProps) {
  return (
    <div className="d-flex mb-3">
      <p className="fs-14 fw-300 primary-color m-0 w-50">{label}</p>
      <p className="fs-14 fw-600 primary-color m-0 w-50 break-word">{value}</p>
    </div>
  );
}
